import React from 'react';
import { FaLock, FaBolt, FaShieldAlt, FaInfinity } from 'react-icons/fa';

/**
 * Wiederverwendbare ZenPost Ollama/Local AI Section Komponente
 * Zeigt die Vorteile der lokalen KI-Integration
 */
const ZenPostOllamaSection = ({
  badge = "Lokale KI",
  title = "KI auf deinem Gerät",
  subtitle = "Ollama-Integration. Offline nutzbar. Keine API-Kosten.",
  features = null,
  models = null,
  howItWorksTitle = "So funktioniert es",
  howItWorksDescription = "Installiere Ollama einmal. ZenPost Studio erkennt es automatisch. Die Modelle laufen auf deinem Mac, Windows oder Linux.",
  theme = "light"
}) => {
  // Default Features
  const defaultFeatures = [
    {
      icon: <FaLock className="text-2xl sm:text-4xl text-brand mx-auto mb-2 sm:mb-4" />,
      title: "100% Offline",
      description: "Keine Internet-Verbindung nötig"
    },
    {
      icon: <FaBolt className="text-2xl sm:text-4xl text-brand mx-auto mb-2 sm:mb-4" />,
      title: "Keine API-Kosten",
      description: "Spare 30-50€/Monat pro User"
    },
    {
      icon: <FaShieldAlt className="text-2xl sm:text-4xl text-brand mx-auto mb-2 sm:mb-4" />,
      title: "Volle Privatsphäre",
      description: "Deine Daten bleiben bei dir"
    },
    {
      icon: <FaInfinity className="text-2xl sm:text-4xl text-brand mx-auto mb-2 sm:mb-4" />,
      title: "Unbegrenzt",
      description: "Keine Token-Limits"
    }
  ];

  // Default Models
  const defaultModels = [
    "LLaMA 2",
    "Mistral",
    "CodeLlama",
    "Custom Models"
  ];

  const featureList = features || defaultFeatures;
  const modelList = models || defaultModels;

  const isDark = theme === "dark";

  return (
    <section className={isDark ? "py-16 px-4 bg-[transparent] text-brand " : "py-16 px-4 bg-gradient-to-r from-purple-50 via-white to-blue-50"}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-4">
          <div className={isDark ? "inline-block px-3 py-1.5 sm:px-4 sm:py-2  rounded-full border border-brand mb-3 sm:mb-4" : "inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-100 rounded-full mb-3 sm:mb-4"}>
            <span className={isDark ? "text-[#EFEBDC]  text-sm sm:text-xs" : "  text-sm sm:text-base"}>{badge}</span>
          </div>
          <h2 className={isDark ? "text-2xl sm:text-3xl md:text-5xl font-extrabold text-brand mb-3 sm:mb-4" : "text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4"}>
            {title}
          </h2>
          <p className={isDark ? "text-sm sm:text-lg md:text-lg text-[#EFEBDC] max-w-3xl mx-auto" : "text-sm sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"}>
            {subtitle}
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 px-4">
          {featureList.map((feature, index) => (
            <div
              key={index}
              className={isDark ? " border border-brand p-4 sm:p-6 rounded-lg shadow-xl shadow-slate-500/20 text-center hover:scale-105 transition-transform" : "bg-white p-4 sm:p-6 rounded-lg shadow-md text-center hover:scale-105 transition-transform"}
            >
              {feature.icon}
              <h3 className={isDark ? "font-bold text-brand mb-1 sm:mb-2 text-sm sm:text-base" : "font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base"}>
                {feature.title}
              </h3>
              <p className={isDark ? "text-xs sm:text-sm text-[#EFEBDC]" : "text-xs sm:text-sm text-gray-600"}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* How It Works Card */}
        <div className={isDark ? " text-brand rounded-2xl p-6 sm:p-8 md:p-12 shadow-2xl mx-4 border border-brand/30" : "bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-2xl mx-4"}>
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-black mb-3 sm:mb-4">
                {howItWorksTitle}
              </h3>
              <p className={isDark ? "text-sm sm:text-sm md:text-sm mb-4 sm:mb-6 text-[#EFEBDC]" : "text-sm sm:text-base md:text-lg mb-4 sm:mb-6 text-slate-200"}>
                {howItWorksDescription}
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {modelList.map((model, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-transparent border border-brand rounded-lg text-xs sm:text-sm font-medium"
                  >
                    {model}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="text-5xl sm:text-6xl md:text-8xl">🧠</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ZenPostOllamaSection;
